import { ROUND_NAMES } from './constants.js';

/**
 * Fisher-Yates shuffle algorithm
 * @param {Array} candidates - Array of candidates to shuffle
 * @returns {Array} Shuffled array
 */
export function shuffleCandidates(candidates) {
  const shuffled = [...candidates];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Get round name based on number of remaining candidates
 * @param {number} size - Number of candidates in the round
 * @param {string} language - Language code ('ko' or 'en')
 * @returns {string} Round name in specified language
 */
export function getRoundName(size, language = 'ko') {
  const names = ROUND_NAMES[language] || ROUND_NAMES.ko;
  return names[size] || (language === 'ko' ? `${size}강` : `Round of ${size}`);
}

/**
 * Create initial tournament bracket
 * @param {Array} candidates - Array of candidate objects
 * @param {number} tournamentSize - Size of tournament (must be power of 2)
 * @returns {Object} Initial round object with matches
 */
export function createInitialBracket(candidates, tournamentSize) {
  const shuffled = shuffleCandidates(candidates);
  const participants = shuffled.slice(0, tournamentSize);

  const matches = [];
  for (let i = 0; i < participants.length; i += 2) {
    matches.push({
      matchId: i / 2,
      candidate1: participants[i],
      candidate2: participants[i + 1],
      winner: null,
    });
  }

  return {
    roundNumber: 1,
    roundName: getRoundName(tournamentSize),
    matches,
  };
}

/**
 * Create next round from winners of previous round
 * @param {Object} previousRound - Previous round object
 * @param {number} roundNumber - Next round number
 * @returns {Object} Next round object with matches
 */
export function createNextRound(previousRound, roundNumber) {
  const winners = previousRound.matches
    .map(match => match.winner)
    .filter(winner => winner !== null);

  if (winners.length < 2) {
    return null; // Tournament is complete
  }

  const matches = [];
  for (let i = 0; i < winners.length; i += 2) {
    matches.push({
      matchId: i / 2,
      candidate1: winners[i],
      candidate2: winners[i + 1],
      winner: null,
    });
  }

  return {
    roundNumber,
    roundName: getRoundName(winners.length),
    matches,
  };
}
